import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, withCsrfHeader } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import type { UserProfile } from "@shared/schema";

interface TutorialModeContextValue {
  enabled: boolean;
  toggle: (next?: boolean) => void;
  isPending: boolean;
}

const TutorialModeContext = createContext<TutorialModeContextValue | null>(null);

interface TutorialModeProviderProps {
  children: ReactNode;
}

/**
 * Provides the per-user tutorial-mode flag (the in-app `?` hint icons).
 *
 * Both the read and the write intentionally bypass impersonation by using
 * raw fetch (no impersonation headers via the shared apiRequest helper).
 * That way, when an admin is "viewing as" another user, their own tutorial
 * setting is shown and any toggles they make stay on their own profile —
 * we never accidentally write to the impersonated user's record.
 */
export function TutorialModeProvider({ children }: TutorialModeProviderProps) {
  const { user } = useAuth();
  const { data: profile } = useQuery<UserProfile | null>({
    // Same query key used by App.tsx for the "real" (non-impersonated)
    // profile, so the cache is shared and a toggle here updates the
    // sidebar avatar / admin gates immediately.
    queryKey: ["/api/profile-real"],
    queryFn: async () => {
      const res = await fetch("/api/profile", { credentials: "include" });
      if (!res.ok) return null;
      return (await res.json()) as UserProfile;
    },
    // Critical: only fetch once we know the user is signed in. Otherwise an
    // unauthenticated render (e.g. the very first paint, or the public
    // landing page) would cache `null` under this key. The global cache uses
    // an effectively-infinite stale time, so a stale `null` would suppress
    // the onboarding wizard and admin-only UI even after the user signs in.
    enabled: !!user,
  });

  const mutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const res = await fetch("/api/profile/tutorial-mode", {
        method: "PATCH",
        headers: await withCsrfHeader({ "Content-Type": "application/json" }),
        credentials: "include",
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) {
        throw new Error(`Failed to update tutorial mode (${res.status})`);
      }
      return (await res.json()) as UserProfile;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/profile-real"], data);
      // Also nudge the impersonation-aware key in case the user is viewing
      // their own profile (in which case both keys point at the same row).
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/profile-real"] });
    },
  });

  // Platform-wide override: tutorial-mode hints are hard-off for every user
  // (see "Help Tooltips (Tutorial Mode)" entry in /platform/enhancements).
  // The per-user `tutorialModeEnabled` profile flag is preserved on the
  // backend so a future re-enable just deletes this line — no migration.
  void profile;
  const enabled = false;

  const value = useMemo<TutorialModeContextValue>(
    () => ({
      enabled,
      isPending: mutation.isPending,
      toggle: (next?: boolean) => {
        mutation.mutate(next === undefined ? !enabled : next);
      },
    }),
    [enabled, mutation],
  );

  return <TutorialModeContext.Provider value={value}>{children}</TutorialModeContext.Provider>;
}

export function useTutorialMode(): TutorialModeContextValue {
  const ctx = useContext(TutorialModeContext);
  if (!ctx) {
    // Outside of provider — match the platform-wide hard-off default.
    return { enabled: false, toggle: () => {}, isPending: false };
  }
  return ctx;
}
